import React, { useCallback, useEffect, useRef, useState } from "react";
import useInterval from "use-interval";
import { useIsSmallScreen } from "../App";
import { PointedSyllable, PointedText } from "../models";
import { Padding } from "../shared-components/Padding";
import {
    BACKGROUND_COLOR,
    DOT_COLOR,
    DROP_CAP_CANVAS_FONT,
    HYPHEN_COLOR,
    LINE_COLOR,
    MAIN_CANVAS_FONT,
    MAIN_CANVAS_FONT_SMALL,
    NOTE_COLOR,
    RUBRIC_COLOR,
    STAVE_DIVIDER_COLOR,
    TEXT_COLOR,
    TEXT_FONT,
} from "../tokens";
import { useSettings } from "../utils/settings";
import { ImageDataViewer } from "./ImageDataViewer";
import {
    drawDiamond,
    drawLongDot,
    drawNote,
    drawStaveLines,
} from "./chant-utils";
import { EBChant } from "./eb-chant/EBChant";
import {
    NOTES,
    SHARP_MAP,
    getTransposeByFromMode,
    transposeNote,
} from "./eb-chant/note-utils";

type RenderElement =
    | { type: "clef"; variant: "f5" | "c5" | "c7" }
    | { type: "drop cap"; text: string }
    | {
          type: "syllable";
          text: string;
          notes: string[];
          width: number;
          long: boolean;
          reciting: boolean;
          highlightedNoteCount: number;
      }
    | { type: "space" }
    | { type: "qbar" }
    | { type: "mid" }
    | { type: "end" }
    | { type: "line feed"; noteValue: number }
    | { type: "hyphen" }
    | { type: "dot"; long: boolean };

interface RenderLine {
    elements: RenderElement[];
    textWidth: number;
    rawWidth: number;
    spaceCount: number;
}

export type BoldMode = "none" | "alternate" | "alternate halves" | "all";

export const LINE_HEIGHT = 76;
export const STAVE_SPACING = 11;
export const HALF_NOTE_SIZE = 2.5;
export const NOTE_SIZE = HALF_NOTE_SIZE * 2 + 1;
export const NOTE_CURVE_OFFSET = 2;
export const CLEF_WIDTH = STAVE_SPACING * 1.5;

export const getNoteValue = (note: string) => {
    return parseInt(note.replace("b", ""));
};

const WINDOW: any = window;

export const Chant: React.FC<{
    pointedText: PointedText;
    boldMode: BoldMode;
    dropCap: boolean;
    highlightCount?: number;
    rubric?: string;
    noPadding?: boolean;
    yExtraOffset?: number;
    exportLines?: boolean;
    tone?: string;
    onReportMistake?: (verseIndex: number) => void;
}> = (props) => {
    const {
        pointedText,
        boldMode,
        dropCap,
        highlightCount,
        rubric,
        yExtraOffset,
        exportLines,
        tone,
    } = props;

    const { settings, setSetting } = useSettings();

    useEffect(() => {
        if (settings.tuning === "enabled") {
            WINDOW.startTuning();
        }
    }, [settings.tuning]);

    const [tuningNote, setTuningNote] = useState<number | undefined>(undefined);

    const getTuningNote = useCallback(() => {
        if (settings.tuning === "enabled" && tone) {
            const transposeByKey = tone[0];

            const tuningEvent = WINDOW.tuningEvent;

            if (tuningEvent && tuningEvent.note) {
                const transposeBy =
                    settings.transpositions["1"] +
                    getTransposeByFromMode(transposeByKey);

                const clefOffset = pointedText.clef === "c7" ? 7 : 5;

                const offset =
                    pointedText.clef === "c5" || pointedText.clef === "c7"
                        ? 9 - clefOffset
                        : 5 - clefOffset;

                const detectedNote = SHARP_MAP[tuningEvent.note];

                const transposedNote = transposeNote(
                    `${detectedNote}${tuningEvent.octave}`,
                    -transposeBy
                );

                const isFlat = transposedNote[1] === "b";

                const transposedNoteWithoutFlat = transposedNote.replace(
                    "b",
                    ""
                );

                const noteValue =
                    NOTES.findIndex((n) => n === transposedNoteWithoutFlat) -
                    offset -
                    (isFlat ? 0.5 : 0);

                if (noteValue && noteValue > -0.5) {
                    setTuningNote(noteValue);
                }
            }
        }
    }, [settings]);

    useInterval(getTuningNote, 25);

    const noPadding = exportLines || props.noPadding;

    const isSmallScreen = useIsSmallScreen();
    const CANVAS_FONT = exportLines
        ? "18px Palatino"
        : isSmallScreen
        ? MAIN_CANVAS_FONT_SMALL
        : MAIN_CANVAS_FONT;
    const CANVAS_SMALL_CAP_FONT = exportLines
        ? "14px Palatino"
        : isSmallScreen
        ? `12px ${TEXT_FONT}`
        : `14px ${TEXT_FONT}`;

    const SPACE_WIDTH = isSmallScreen ? 5 : 7;
    const SPACES_PER_MID = isSmallScreen ? 3 : 3;
    const SPACES_PER_END = isSmallScreen ? 5 : 5;
    const TEXT_BREATHING_ROOM = isSmallScreen ? 35 : 50;

    const containerRef = useRef<HTMLDivElement>(null);

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const [height, setHeight] = useState(0);

    const canvas = canvasRef.current;
    useEffect(() => {
        return () => {
            if (canvas) {
                canvas.height = 0;
                canvas.width = 0;
            }
        };
    }, [canvas]);

    const [forceRenderCount, setForceRenderCount] = useState(0);
    useInterval(
        useCallback(() => {
            if (forceRenderCount < 10) {
                setForceRenderCount((rc) => rc + 1);
            }
        }, [forceRenderCount]),
        2500
    );

    const [exportData, setExportData] = useState<{
        width: number;
        height: Number;
        lines: ImageData[];
    }>({ width: 0, height: 0, lines: [] });

    useEffect(() => {
        if (!canvasRef.current) {
            return;
        }

        const context = canvasRef.current.getContext("2d");

        if (!context) {
            return;
        }

        const canvasWidth = canvasRef.current.getBoundingClientRect().width;

        // Scale correctly
        const ctx: any = context;
        const dpr = exportLines ? 3 : window.devicePixelRatio || 1;
        const bsr =
            ctx.webkitBackingStorePixelRatio ||
            ctx.mozBackingStorePixelRatio ||
            ctx.msBackingStorePixelRatio ||
            ctx.oBackingStorePixelRatio ||
            ctx.backingStorePixelRatio ||
            1;
        let pixelRatio = dpr / bsr;

        let pixelWidth = canvasWidth * pixelRatio;
        let pixelHeight = height * pixelRatio;

        //while (pixelWidth * pixelHeight > 16777216 && pixelRatio >= 2) {
        while (pixelHeight * pixelWidth > 2048 * 4096 && pixelRatio >= 2) {
            // Don't exceed maximum size in safari
            pixelRatio -= 0.5;

            pixelWidth = canvasWidth * pixelRatio;
            pixelHeight = height * pixelRatio;
        }

        while (
            navigator.userAgent.indexOf("Firefox") > 0 &&
            pixelHeight > 32767
        ) {
            // Don't exceed maximum size in firefox
            pixelRatio--;

            pixelWidth = canvasWidth * pixelRatio;
            pixelHeight = height * pixelRatio;
        }

        canvasRef.current.width = pixelWidth;
        canvasRef.current.height = pixelHeight;

        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.font = CANVAS_FONT;

        context.clearRect(
            0,
            0,
            canvasRef.current.width,
            canvasRef.current.height
        );

        // Compute the line layout
        const renderLines: RenderLine[] = [
            { elements: [], rawWidth: 0, textWidth: 0, spaceCount: 0 },
        ];

        const addHyphen = () => {
            context.font = CANVAS_FONT;
            renderLines[renderLines.length - 1].elements.push({
                type: "hyphen",
            });
            renderLines[renderLines.length - 1].textWidth +=
                context.measureText("-").width;
            renderLines[renderLines.length - 1].rawWidth +=
                context.measureText("-").width;
        };

        const addDot = (long: boolean) => {
            context.font = CANVAS_FONT;
            renderLines[renderLines.length - 1].elements.push({
                type: "dot",
                long,
            });
            renderLines[renderLines.length - 1].textWidth +=
                context.measureText("-").width;
            renderLines[renderLines.length - 1].rawWidth +=
                context.measureText("-").width;
        };

        const addClef = () => {
            renderLines[renderLines.length - 1].elements.push({
                type: "clef",
                variant: pointedText.clef,
            });
            renderLines[renderLines.length - 1].textWidth += CLEF_WIDTH;
            renderLines[renderLines.length - 1].rawWidth += CLEF_WIDTH;
        };

        let isFirstSyllable = true;
        let verseIsBold = boldMode === "all";
        let highlightedSoFar = 0;
        const processSyllable = (
            syllable: PointedSyllable,
            verseIndex: number,
            half: "first" | "second"
        ) => {
            if (isFirstSyllable) {
                if (dropCap) {
                    // Add drop cap at the very beginning
                    renderLines[renderLines.length - 1].elements.push({
                        type: "drop cap",
                        text: syllable.text[0],
                    });
                    context.font = DROP_CAP_CANVAS_FONT;
                    const dropCapWidth = context.measureText(
                        syllable.text[0]
                    ).width;
                    renderLines[renderLines.length - 1].textWidth +=
                        dropCapWidth;
                    renderLines[renderLines.length - 1].rawWidth +=
                        dropCapWidth;
                    syllable = {
                        ...syllable,
                        text: syllable.text.substr(1),
                    };
                }

                isFirstSyllable = false;

                // Add the first clef
                addClef();
            }

            if (syllable.qBarBefore) {
                renderLines[renderLines.length - 1].elements.push({
                    type: "qbar",
                });
                renderLines[renderLines.length - 1].rawWidth += 20;
            }

            context.font = (verseIsBold ? "700 " : "500 ") + CANVAS_FONT;
            let syllableWidth = Math.max(
                context.measureText(syllable.text).width,
                NOTE_SIZE * syllable.notes.length * 0.75
            );
            if (syllable.text.slice(0, 4) === "LORD") {
                syllableWidth *= 0.9;
            }

            const highlightedNoteCount = Math.min(
                (highlightCount ?? 0) - highlightedSoFar,
                syllable.notes.length
            );
            highlightedSoFar += highlightedNoteCount;
            const syllableElement: RenderElement = {
                type: "syllable",
                text: syllable.text,
                notes: syllable.notes,
                long: syllable.long,
                width: syllableWidth,
                reciting: !!syllable.reciting,
                highlightedNoteCount,
            };

            const prevElement: RenderElement | undefined =
                renderLines[renderLines.length - 1]?.elements[
                    renderLines[renderLines.length - 1]?.elements.length - 1
                ] ?? undefined;

            if (
                renderLines[renderLines.length - 1].rawWidth + syllableWidth <
                canvasWidth - TEXT_BREATHING_ROOM
            ) {
                if (
                    prevElement &&
                    prevElement.type === "syllable" &&
                    (prevElement.notes[prevElement.notes.length - 1] !==
                        syllable.notes[0] ||
                        syllable.notes.length > 1)
                ) {
                    if (syllable.dotBefore) {
                        addDot(false);
                    } else {
                        if (
                            prevElement.text[prevElement.text.length - 1] !==
                            "-"
                        ) {
                            addHyphen();
                        }
                    }
                }
                renderLines[renderLines.length - 1].elements.push(
                    syllableElement
                );
                renderLines[renderLines.length - 1].textWidth += syllableWidth;
                renderLines[renderLines.length - 1].rawWidth += syllableWidth;
            } else {
                if (prevElement.type === "syllable") {
                    if (syllable.dotBefore) {
                        addDot(false);
                    } else {
                        if (
                            prevElement.text[prevElement.text.length - 1] !==
                            "-"
                        ) {
                            addHyphen();
                        }
                    }
                }

                renderLines[renderLines.length - 1].elements.push({
                    type: "line feed",
                    noteValue: getNoteValue(syllable.notes[0]),
                });
                renderLines.push({
                    elements: [],
                    rawWidth: 0,
                    textWidth: 0,
                    spaceCount: 0,
                });
                addClef();
                renderLines[renderLines.length - 1].elements.push(
                    syllableElement
                );
                renderLines[renderLines.length - 1].textWidth += syllableWidth;
                renderLines[renderLines.length - 1].rawWidth += syllableWidth;
            }
        };

        const addSpace = () => {
            if (
                renderLines[renderLines.length - 1].rawWidth + SPACE_WIDTH <
                canvasWidth
            ) {
                renderLines[renderLines.length - 1].elements.push({
                    type: "space",
                });
                renderLines[renderLines.length - 1].rawWidth += SPACE_WIDTH;
                renderLines[renderLines.length - 1].spaceCount += 1;
            }
        };

        pointedText.verses.forEach((verse, verseIndex) => {
            if (verse.firstHalf) {
                verse.firstHalf.forEach((word, wordIndex) => {
                    word.syllables.forEach((syllable) =>
                        processSyllable(syllable, verseIndex, "first")
                    );

                    if (!verse.firstHalf) {
                        return; // won't happen, just making TS happy
                    }

                    if (wordIndex < verse.firstHalf.length - 1) {
                        if (
                            verse.firstHalf[wordIndex + 1].syllables[0]
                                .dotBefore
                        ) {
                            addDot(true);
                        } else {
                            addSpace();
                        }
                    }
                });

                renderLines[renderLines.length - 1].elements.push({
                    type: "mid",
                });
                renderLines[renderLines.length - 1].rawWidth +=
                    SPACE_WIDTH * SPACES_PER_MID;
                renderLines[renderLines.length - 1].spaceCount +=
                    SPACES_PER_MID;
            }

            verse.secondHalf.forEach((word, wordIndex) => {
                word.syllables.forEach((syllable) =>
                    processSyllable(syllable, verseIndex, "second")
                );

                if (wordIndex < verse.secondHalf.length - 1) {
                    if (
                        verse.secondHalf[wordIndex + 1].syllables[0].dotBefore
                    ) {
                        addDot(true);
                    } else {
                        addSpace();
                    }
                }
            });

            renderLines[renderLines.length - 1].elements.push({
                type: "end",
            });
            renderLines[renderLines.length - 1].rawWidth +=
                SPACE_WIDTH * SPACES_PER_END;
            renderLines[renderLines.length - 1].spaceCount += SPACES_PER_END;

            if (boldMode === "alternate") {
                verseIsBold = !verseIsBold;
            }
        });

        let bold = boldMode === "all";

        // ==== RENDER THE CONTENT ====
        renderLines.forEach((renderLine, lineIndex) => {
            const spaceWidth =
                (0.2 * (canvasWidth - renderLine.textWidth)) /
                    renderLine.spaceCount +
                0.8 * SPACE_WIDTH;

            let xPos = 0;

            renderLine.elements.forEach((element, elementIndex) => {
                const yPos =
                    lineIndex * LINE_HEIGHT + 0.5 + (yExtraOffset ?? 0);

                switch (element.type) {
                    case "drop cap":
                        context.font = DROP_CAP_CANVAS_FONT;
                        context.fillStyle = LINE_COLOR;
                        context.fillText(
                            element.text,
                            xPos + 4,
                            yPos + LINE_HEIGHT * 0.92
                        );
                        xPos += context.measureText(element.text).width + 4;
                        context.lineWidth = 1;
                        break;

                    case "clef":
                        const clefWidth =
                            element.variant === "f5"
                                ? CLEF_WIDTH * 1.5
                                : CLEF_WIDTH;

                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            clefWidth,
                            tuningNote
                        );

                        let clefX = xPos;
                        const clefY =
                            yPos +
                            ((9 - (element.variant === "c7" ? 7 : 5)) / 2) *
                                STAVE_SPACING;

                        context.fillStyle = NOTE_COLOR;

                        if (element.variant === "f5") {
                            context.beginPath();
                            context.moveTo(
                                clefX + NOTE_SIZE,
                                clefY + HALF_NOTE_SIZE + 1
                            );
                            context.quadraticCurveTo(
                                clefX + HALF_NOTE_SIZE + 0.5,
                                clefY + HALF_NOTE_SIZE + 1 - NOTE_CURVE_OFFSET,
                                clefX,
                                clefY + HALF_NOTE_SIZE + 1
                            );
                            context.lineTo(clefX, clefY - HALF_NOTE_SIZE);
                            context.quadraticCurveTo(
                                clefX + HALF_NOTE_SIZE + 0.5,
                                clefY - HALF_NOTE_SIZE - NOTE_CURVE_OFFSET,
                                clefX + NOTE_SIZE,
                                clefY - HALF_NOTE_SIZE
                            );
                            context.closePath();
                            context.fill();

                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(clefX + NOTE_SIZE - 0.5, clefY);
                            context.lineTo(
                                clefX + NOTE_SIZE - 0.5,
                                clefY + STAVE_SPACING * 1.4
                            );
                            context.closePath();
                            context.stroke();

                            clefX += NOTE_SIZE + 1;
                        }

                        context.beginPath();
                        context.moveTo(clefX, clefY);
                        context.lineTo(clefX, clefY - STAVE_SPACING * 0.5);
                        context.bezierCurveTo(
                            clefX,
                            clefY - STAVE_SPACING * 0.8,
                            clefX + STAVE_SPACING * 0.3,
                            clefY - STAVE_SPACING * 1.0,
                            clefX + STAVE_SPACING * 0.6,
                            clefY - STAVE_SPACING * 0.75
                        );
                        context.lineTo(
                            clefX + STAVE_SPACING * 0.6,
                            clefY - STAVE_SPACING * 0.25
                        );
                        context.bezierCurveTo(
                            clefX + STAVE_SPACING * 0.25,
                            clefY - STAVE_SPACING * 0.25,
                            clefX + 1,
                            clefY - STAVE_SPACING * 0.25,
                            clefX + 1,
                            clefY
                        );

                        context.bezierCurveTo(
                            clefX + 1,
                            clefY + STAVE_SPACING * 0.25,
                            clefX + STAVE_SPACING * 0.25,
                            clefY + STAVE_SPACING * 0.25,
                            clefX + STAVE_SPACING * 0.6,
                            clefY + STAVE_SPACING * 0.25
                        );
                        context.lineTo(
                            clefX + STAVE_SPACING * 0.6,
                            clefY + STAVE_SPACING * 0.75
                        );
                        context.bezierCurveTo(
                            clefX + STAVE_SPACING * 0.3,
                            clefY + STAVE_SPACING * 1.0,
                            clefX,
                            clefY + STAVE_SPACING * 0.8,
                            clefX,
                            clefY + STAVE_SPACING * 0.5
                        );
                        context.closePath();
                        context.fill();

                        xPos += clefWidth;

                        break;

                    case "syllable":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            element.width,
                            tuningNote
                        );

                        let remainingHighlightedNotes =
                            element.highlightedNoteCount;

                        context.fillStyle = TEXT_COLOR;
                        context.font = (bold ? "700 " : "500 ") + CANVAS_FONT;
                        if (element.text.includes("LORD")) {
                            context.font =
                                (bold ? "700 " : "500 ") + CANVAS_FONT;
                            const lWidth = context.measureText("L").width;
                            context.font =
                                (bold ? "700 " : "500 ") +
                                CANVAS_SMALL_CAP_FONT;
                            const remWidth = context.measureText(
                                element.text.slice(1)
                            ).width;
                            context.font =
                                (bold ? "700 " : "500 ") + CANVAS_FONT;
                            context.fillText(
                                "L",
                                xPos + (element.width - lWidth - remWidth) / 2,
                                yPos + 6 * STAVE_SPACING - 2
                            );
                            context.font =
                                (bold ? "700 " : "500 ") +
                                CANVAS_SMALL_CAP_FONT;
                            context.fillText(
                                element.text.slice(1),
                                xPos +
                                    (element.width - lWidth - remWidth) / 2 +
                                    lWidth,
                                yPos + 6 * STAVE_SPACING - 2
                            );
                        } else {
                            context.fillText(
                                element.text,
                                xPos +
                                    (element.width -
                                        context.measureText(element.text)
                                            .width) /
                                        2,
                                yPos + 6 * STAVE_SPACING - 2
                            );
                        }

                        const centerX = Math.round(xPos + element.width / 2);

                        if (element.reciting) {
                            const w = 24;
                            context.beginPath();
                            context.rect(
                                centerX - w / 2,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING -
                                    NOTE_SIZE / 2,
                                w,
                                NOTE_SIZE
                            );
                            if (element.highlightedNoteCount > 0) {
                                context.fillStyle = BACKGROUND_COLOR;
                                context.fill();
                                context.strokeStyle = NOTE_COLOR;
                                context.stroke();
                            } else {
                                context.fillStyle = NOTE_COLOR;
                                context.fill();
                            }
                        } else if (
                            element.notes.length === 2 &&
                            getNoteValue(element.notes[0]) <
                                getNoteValue(element.notes[1])
                        ) {
                            // Two-note ascending
                            drawNote(
                                context,
                                centerX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                centerX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                centerX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                centerX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            if (element.long) {
                                drawLongDot(
                                    context,
                                    centerX,
                                    yPos,
                                    element.notes[0]
                                );
                                drawLongDot(
                                    context,
                                    centerX,
                                    yPos,
                                    element.notes[1]
                                );
                            }
                        } else if (
                            element.notes.length === 2 &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[1])
                        ) {
                            // Two note consecutive descending
                            let noteX = Math.round(
                                centerX -
                                    ((element.notes.length - 1) * NOTE_SIZE) / 2
                            );

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX + NOTE_SIZE,
                                yPos,
                                element.notes[1],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                { shiftFlat: 2 - NOTE_SIZE },
                                tuningNote
                            );
                            context.beginPath();
                            context.moveTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    STAVE_SPACING * 1.3
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            if (element.long) {
                                drawLongDot(
                                    context,
                                    noteX + NOTE_SIZE,
                                    yPos,
                                    element.notes[0]
                                );
                                drawLongDot(
                                    context,
                                    noteX + NOTE_SIZE,
                                    yPos,
                                    `${
                                        getNoteValue(element.notes[1]) -
                                        (getNoteValue(element.notes[1]) % 2)
                                    }`
                                );
                            }
                        } else if (
                            element.notes.length === 2 &&
                            getNoteValue(element.notes[0]) - 2 >=
                                getNoteValue(element.notes[1])
                        ) {
                            // Two note descending by >=2
                            let noteX = Math.round(
                                centerX -
                                    ((element.notes.length - 1) * NOTE_SIZE) / 2
                            );

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX + NOTE_SIZE - 1,
                                yPos,
                                element.notes[1],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                { shiftFlat: 2 - NOTE_SIZE },
                                tuningNote
                            );

                            context.beginPath();
                            context.moveTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            // TODO: implement long dots
                        } else if (
                            (element.notes.length === 3 &&
                                getNoteValue(element.notes[0]) - 1 ===
                                    getNoteValue(element.notes[1]) &&
                                getNoteValue(element.notes[1]) - 1 ===
                                    getNoteValue(element.notes[2])) ||
                            (element.notes.length === 4 &&
                                getNoteValue(element.notes[0]) - 1 ===
                                    getNoteValue(element.notes[1]) &&
                                getNoteValue(element.notes[1]) - 1 ===
                                    getNoteValue(element.notes[2]) &&
                                getNoteValue(element.notes[2]) - 1 ===
                                    getNoteValue(element.notes[3]))
                        ) {
                            // 3 or 4 note descending
                            // Two note consecutive descending
                            let noteX =
                                centerX -
                                ((element.notes.length - 1) * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    STAVE_SPACING * 1.3
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            element.notes.slice(1).forEach((note) => {
                                noteX += NOTE_SIZE - 1;

                                drawDiamond(
                                    context,
                                    noteX,
                                    yPos,
                                    note,
                                    remainingHighlightedNotes-- > 0,
                                    tuningNote
                                );
                            });

                            if (element.long) {
                                drawLongDot(
                                    context,
                                    noteX + 1,
                                    yPos,
                                    element.notes[element.notes.length - 1]
                                );
                            }
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[1]) ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 3 note descender as in the compline resonsory
                            const noteX = centerX - (3 * NOTE_SIZE) / 2;

                            context.beginPath();
                            context.moveTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX - HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    STAVE_SPACING * 1.3
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX + NOTE_SIZE,
                                yPos,
                                element.notes[1],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX + NOTE_SIZE * 2 + 1,
                                yPos,
                                element.notes[2],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            if (element.long) {
                                drawLongDot(
                                    context,
                                    noteX + NOTE_SIZE * 2 + 1,
                                    yPos,
                                    element.notes[2]
                                );
                            }
                        } else if (
                            element.notes.length === 4 &&
                            getNoteValue(element.notes[0]) + 4 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 4 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) + 5 ===
                                getNoteValue(element.notes[3])
                        ) {
                            // 1556 as in Saints day hymn
                            drawNote(
                                context,
                                centerX - HALF_NOTE_SIZE - 1,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                centerX - HALF_NOTE_SIZE - 1,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                centerX,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                centerX,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            drawNote(
                                context,
                                centerX + HALF_NOTE_SIZE + 1,
                                yPos,
                                element.notes[2],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                centerX + HALF_NOTE_SIZE + 1,
                                yPos,
                                element.notes[3],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                centerX +
                                    HALF_NOTE_SIZE +
                                    1 +
                                    HALF_NOTE_SIZE +
                                    1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[2])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                centerX +
                                    HALF_NOTE_SIZE +
                                    1 +
                                    HALF_NOTE_SIZE +
                                    1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[3])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();
                        } else if (
                            element.notes.length === 4 &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[3])
                        ) {
                            // 5765 as in Saints day hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2 + 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 4 &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) - 2 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[3])
                        ) {
                            // 4323 as in annunciation hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2 + 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    STAVE_SPACING * 1.3
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            noteX += NOTE_SIZE - 2;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            noteX += NOTE_SIZE + 1;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[3],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 4 &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[3])
                        ) {
                            // 4543 as in annunciation hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            noteX += NOTE_SIZE - 2;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 132 as in Sunday evening OT hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE - 1;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 345 as in passiontide evening hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -0.5,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[2])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();
                        } else if (
                            element.notes.length === 6 &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) - 3 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) - 2 ===
                                getNoteValue(element.notes[3]) &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[4]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[5])
                        ) {
                            // 431234 as in passiontide evening hymn
                            let noteX = centerX - (5 * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[3])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            noteX += NOTE_SIZE - 2;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            noteX += NOTE_SIZE + 2;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[3],
                                0.5,
                                0,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[4],
                                -0.5,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[3])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[4])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[5],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[5])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[3])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();
                        } else if (
                            element.notes.length === 5 &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 3 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[3]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[4])
                        ) {
                            // 35654 as in passiontide evening hymn
                            let noteX = centerX - (4 * NOTE_SIZE) / 2;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                0,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                { shiftFlat: -4 },
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[2])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[4])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 2;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[4],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 121 as in Office Hymn "Amen"
                            let noteX = centerX - (3 * NOTE_SIZE) / 2;

                            context.beginPath();
                            context.moveTo(
                                noteX + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    STAVE_SPACING * 1.3
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            context.beginPath();
                            context.moveTo(
                                noteX,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING -
                                    HALF_NOTE_SIZE
                            );
                            context.quadraticCurveTo(
                                noteX + NOTE_SIZE * 2 + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING -
                                    HALF_NOTE_SIZE +
                                    1,
                                noteX + NOTE_SIZE * 3 + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING -
                                    HALF_NOTE_SIZE +
                                    1
                            );
                            context.lineTo(
                                noteX + NOTE_SIZE * 3 + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING +
                                    HALF_NOTE_SIZE +
                                    1
                            );
                            context.quadraticCurveTo(
                                noteX + NOTE_SIZE * 2 + 0.5,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING +
                                    HALF_NOTE_SIZE +
                                    1,
                                noteX,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING +
                                    HALF_NOTE_SIZE
                            );
                            const highlighted =
                                (remainingHighlightedNotes -= 2) > 0;
                            context.fillStyle = highlighted
                                ? "white"
                                : NOTE_COLOR;
                            context.fill();
                            if (highlighted) {
                                context.strokeStyle = "black";
                                context.stroke();
                            }

                            context.beginPath();
                            context.moveTo(
                                noteX + NOTE_SIZE * 3,
                                yPos +
                                    ((9 - getNoteValue(element.notes[2])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + NOTE_SIZE * 3,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.stroke();

                            drawNote(
                                context,
                                noteX + NOTE_SIZE * 2 + HALF_NOTE_SIZE,
                                yPos,
                                element.notes[2],
                                -1,
                                1,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 5 &&
                            getNoteValue(element.notes[0]) + 3 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[3]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[4])
                        ) {
                            // 25432 as in Let us bless the Lord Alleluia Alleluia
                            let noteX = centerX - (4 * NOTE_SIZE) / 2 + 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            // First diamond streak
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX - 2,
                                yPos,
                                element.notes[4],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 5 &&
                            getNoteValue(element.notes[0]) + +1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) + 3 ===
                                getNoteValue(element.notes[3]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[4])
                        ) {
                            // 45675 as in Ascension hymn
                            let noteX = centerX - (4 * NOTE_SIZE) / 2 + 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[3],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[3])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[4])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE - 1;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[4],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 6 &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[2]) &&
                            getNoteValue(element.notes[0]) + 1 ===
                                getNoteValue(element.notes[3]) &&
                            getNoteValue(element.notes[0]) ===
                                getNoteValue(element.notes[4]) &&
                            getNoteValue(element.notes[0]) - 1 ===
                                getNoteValue(element.notes[5])
                        ) {
                            // 567654 as in Ascension hymn
                            let noteX = centerX - (4 * NOTE_SIZE) / 2 + 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            // First diamond streak
                            noteX += NOTE_SIZE;

                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX - 2,
                                yPos,
                                element.notes[4],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE - 1;
                            drawDiamond(
                                context,
                                noteX - 3,
                                yPos,
                                element.notes[5],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) + 3 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 2 ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 476 as in Ascension hymn
                            let noteX = centerX - (3 * NOTE_SIZE) / 2 + 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                -0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            noteX += NOTE_SIZE - 1;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (
                            element.notes.length === 3 &&
                            getNoteValue(element.notes[0]) + 4 ===
                                getNoteValue(element.notes[1]) &&
                            getNoteValue(element.notes[0]) + 5 ===
                                getNoteValue(element.notes[2])
                        ) {
                            // 378 as in Whitsun PM hymn
                            let noteX = centerX - (2 * NOTE_SIZE) / 2 + 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                -0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );

                            noteX += NOTE_SIZE;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                        } else if (element.notes.length === 9) {
                            // HACKY SPECIAL CASE FOR "...APPLE OF THINE EYE" - 9 NOTES LONG
                            let noteX = centerX - (7 * NOTE_SIZE) / 2;

                            // First ascender
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[0],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[1],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[0])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[1])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            // First diamond streak
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[2],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[3],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX - 2,
                                yPos,
                                element.notes[4],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            // Second ascender
                            noteX += NOTE_SIZE + 2;
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[5],
                                0.5,
                                0.5,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            drawNote(
                                context,
                                noteX,
                                yPos,
                                element.notes[6],
                                -1,
                                undefined,
                                remainingHighlightedNotes-- > 0,
                                {},
                                tuningNote
                            );
                            context.strokeStyle = NOTE_COLOR;
                            context.beginPath();
                            context.moveTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[5])) / 2) *
                                        STAVE_SPACING
                            );
                            context.lineTo(
                                noteX + HALF_NOTE_SIZE + 1,
                                yPos +
                                    ((9 - getNoteValue(element.notes[6])) / 2) *
                                        STAVE_SPACING
                            );
                            context.stroke();

                            // Second diamond streak
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX,
                                yPos,
                                element.notes[7],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );
                            noteX += NOTE_SIZE;
                            drawDiamond(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[8],
                                remainingHighlightedNotes-- > 0,
                                tuningNote
                            );

                            drawLongDot(
                                context,
                                noteX - 1,
                                yPos,
                                element.notes[8]
                            );
                        } else {
                            // Any other note(s)
                            let noteX =
                                centerX -
                                ((element.notes.length - 1) * NOTE_SIZE) / 2;

                            element.notes.forEach((note) => {
                                drawNote(
                                    context,
                                    noteX,
                                    yPos,
                                    note,
                                    0,
                                    undefined,
                                    remainingHighlightedNotes-- > 0,
                                    {},
                                    tuningNote
                                );

                                noteX += NOTE_SIZE;
                            });

                            if (element.long) {
                                drawLongDot(
                                    context,
                                    noteX - NOTE_SIZE,
                                    yPos,
                                    element.notes[element.notes.length - 1]
                                );
                            }
                        }

                        xPos += element.width;
                        break;

                    case "space":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            spaceWidth,
                            tuningNote
                        );
                        xPos += spaceWidth;
                        break;

                    case "qbar":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            spaceWidth * SPACES_PER_MID * 0.5,
                            tuningNote
                        );
                        context.strokeStyle = STAVE_DIVIDER_COLOR;
                        context.beginPath();
                        context.moveTo(
                            Math.round(
                                xPos + (spaceWidth * SPACES_PER_MID * 0.5) / 2
                            ) + 0.5,
                            yPos + 1.5 * STAVE_SPACING - 0.5
                        );
                        context.lineTo(
                            Math.round(
                                xPos + (spaceWidth * SPACES_PER_MID * 0.5) / 2
                            ) + 0.5,
                            yPos + 3.5 * STAVE_SPACING + 0.5
                        );
                        context.stroke();
                        xPos += spaceWidth * SPACES_PER_MID * 0.5;

                        break;

                    case "mid":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            spaceWidth * SPACES_PER_MID,
                            tuningNote
                        );
                        context.strokeStyle = STAVE_DIVIDER_COLOR;
                        context.beginPath();
                        context.moveTo(
                            Math.round(
                                xPos + (spaceWidth * SPACES_PER_MID) / 2
                            ) + 0.5,
                            yPos + STAVE_SPACING - 0.5
                        );
                        context.lineTo(
                            Math.round(
                                xPos + (spaceWidth * SPACES_PER_MID) / 2
                            ) + 0.5,
                            yPos + 4 * STAVE_SPACING + 0.5
                        );
                        context.stroke();
                        xPos += spaceWidth * SPACES_PER_MID;

                        if (boldMode === "alternate halves") {
                            bold = !bold;
                        }

                        break;

                    case "end":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            elementIndex === renderLine.elements.length - 1
                                ? (spaceWidth * SPACES_PER_END) / 2 +
                                      STAVE_SPACING / 4
                                : spaceWidth * SPACES_PER_END,
                            tuningNote
                        );
                        context.strokeStyle = STAVE_DIVIDER_COLOR;
                        context.beginPath();
                        context.moveTo(
                            Math.round(
                                xPos +
                                    (spaceWidth * SPACES_PER_END) / 2 -
                                    STAVE_SPACING / 4
                            ) + 0.5,
                            yPos + STAVE_SPACING - 0.5
                        );
                        context.lineTo(
                            Math.round(
                                xPos +
                                    (spaceWidth * SPACES_PER_END) / 2 -
                                    STAVE_SPACING / 4
                            ) + 0.5,
                            yPos + 4 * STAVE_SPACING + 0.5
                        );
                        context.stroke();
                        context.beginPath();
                        context.moveTo(
                            Math.round(
                                xPos +
                                    (spaceWidth * SPACES_PER_END) / 2 +
                                    STAVE_SPACING / 4
                            ) + 0.5,
                            yPos + STAVE_SPACING - 0.5
                        );
                        context.lineTo(
                            Math.round(
                                xPos +
                                    (spaceWidth * SPACES_PER_END) / 2 +
                                    STAVE_SPACING / 4
                            ) + 0.5,
                            yPos + 4 * STAVE_SPACING + 0.5
                        );
                        context.stroke();
                        xPos += spaceWidth * SPACES_PER_END;

                        if (
                            boldMode === "alternate" ||
                            boldMode === "alternate halves"
                        ) {
                            bold = !bold;
                        }

                        break;

                    case "hyphen":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            context.measureText("-").width,
                            tuningNote
                        );
                        context.fillStyle = HYPHEN_COLOR;
                        context.fillText(
                            "-",
                            xPos,
                            yPos + 6 * STAVE_SPACING - 2
                        );
                        xPos += context.measureText("-").width;
                        break;

                    case "dot":
                        const dotWidth = element.long
                            ? context.measureText(" • ").width
                            : context.measureText("•").width;
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            dotWidth,
                            tuningNote
                        );
                        context.fillStyle = DOT_COLOR;
                        context.font = CANVAS_FONT;
                        context.fillText(
                            "•",
                            xPos +
                                dotWidth / 2 -
                                context.measureText("•").width / 2,
                            yPos + 6 * STAVE_SPACING - 0.5
                        );
                        xPos += dotWidth;
                        break;

                    case "line feed":
                        drawStaveLines(
                            context,
                            xPos,
                            yPos,
                            canvasWidth - xPos,
                            tuningNote
                        );
                        context.fillStyle = NOTE_COLOR;
                        context.beginPath();
                        context.moveTo(
                            canvasWidth - HALF_NOTE_SIZE - 0.5,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING -
                                HALF_NOTE_SIZE -
                                1
                        );
                        context.quadraticCurveTo(
                            canvasWidth - HALF_NOTE_SIZE / 2,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING -
                                HALF_NOTE_SIZE -
                                1 +
                                NOTE_CURVE_OFFSET / 2,
                            canvasWidth,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING -
                                HALF_NOTE_SIZE -
                                1 +
                                NOTE_CURVE_OFFSET / 2
                        );
                        context.lineTo(
                            canvasWidth,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING +
                                HALF_NOTE_SIZE +
                                NOTE_CURVE_OFFSET / 2
                        );
                        context.quadraticCurveTo(
                            canvasWidth - HALF_NOTE_SIZE / 2,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING +
                                HALF_NOTE_SIZE +
                                NOTE_CURVE_OFFSET / 2,
                            canvasWidth - HALF_NOTE_SIZE - 0.5,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING +
                                HALF_NOTE_SIZE
                        );
                        context.closePath();
                        context.fill();

                        context.beginPath();
                        context.strokeStyle = NOTE_COLOR;
                        context.moveTo(
                            canvasWidth - 0.5,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING +
                                HALF_NOTE_SIZE +
                                NOTE_CURVE_OFFSET / 2 -
                                2
                        );
                        context.lineTo(
                            canvasWidth - 0.5,
                            yPos +
                                ((9 - element.noteValue) / 2) * STAVE_SPACING -
                                HALF_NOTE_SIZE * 5 +
                                NOTE_CURVE_OFFSET / 2
                        );
                        context.stroke();

                        break;
                }
            });
        });

        if (exportLines && canvasWidth > 0) {
            const lineImageData: ImageData[] = [];

            let exportY = yExtraOffset ?? 0;

            renderLines.forEach(() => {
                lineImageData.push(
                    context.getImageData(
                        0,
                        exportY,
                        canvasWidth * pixelRatio,
                        LINE_HEIGHT * pixelRatio
                    )
                );

                exportY += LINE_HEIGHT * pixelRatio;
            });

            console.log("lines", {
                lines: lineImageData,
                width: canvasWidth * pixelRatio,
                height: LINE_HEIGHT * pixelRatio,
            });

            setExportData({
                lines: lineImageData,
                width: canvasWidth * pixelRatio,
                height: LINE_HEIGHT * pixelRatio,
            });
        }

        setHeight(renderLines.length * LINE_HEIGHT);
    }, [pointedText, height, boldMode, forceRenderCount, tuningNote]);

    const isOrdinariatePage = window.location.href.includes("/se/");

    const chant =
        settings.experimentalChantRendering === "on" || isOrdinariatePage ? (
            <EBChant
                gabc={getGabcFromPointedText(pointedText, boldMode)}
                dropCap={dropCap}
                tone={tone}
                onReportMistake={props.onReportMistake}
            />
        ) : (
            <>
                <div
                    ref={containerRef}
                    style={{
                        width: "100%",
                    }}
                >
                    <canvas ref={canvasRef} style={{ width: "100%", height }} />
                </div>
                {exportData.width > 0 ? (
                    exportData.lines.map((imageData) => {
                        return (
                            <div id="export-item">
                                <ImageDataViewer
                                    imageData={imageData}
                                    width={imageData.width}
                                    height={imageData.height}
                                />
                            </div>
                        );
                    })
                ) : (
                    <></>
                )}
            </>
        );

    const content = rubric ? (
        <>
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    flexDirection: isSmallScreen ? "column" : "row",
                }}
            >
                <div
                    style={
                        isSmallScreen
                            ? { color: RUBRIC_COLOR, fontSize: "90%" }
                            : {
                                  color: RUBRIC_COLOR,
                                  paddingRight: 16,
                                  paddingTop: 16,
                                  width: 64,
                                  flexShrink: 0,
                                  textAlign: "right",
                                  fontSize: "90%",
                              }
                    }
                >
                    {rubric}
                </div>
                {chant}
            </div>
        </>
    ) : (
        chant
    );

    const paddedContent = noPadding ? (
        content
    ) : (
        <Padding onlySides>{content}</Padding>
    );

    return paddedContent;
};

const lookup = "cdefghijk";

const getGabcNotesForSyllable = (notes: string[], long: boolean) => {
    let result = "";

    const drawNote = (note: string, long: boolean) => {
        if (note[note.length - 1] === "b") {
            result = lookup[getNoteValue(note)] + "x" + result;
        }

        result += lookup[getNoteValue(note)];

        if (long) {
            result += ".";
        }
    };

    const drawDiamond = (note: string, long: boolean) => {
        result += lookup[getNoteValue(note)].toLocaleUpperCase();

        if (long) {
            result += ".";
        }
    };

    if (notes.length === 2 && getNoteValue(notes[0]) < getNoteValue(notes[1])) {
        // Two-note ascending
        drawNote(notes[0], long);
        drawNote(notes[1], long);
    } else if (
        notes.length === 2 &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[1])
    ) {
        // Two note consecutive descending
        drawNote(notes[0], long);
        drawNote(notes[1], long);
    } else if (
        notes.length === 2 &&
        getNoteValue(notes[0]) - 2 >= getNoteValue(notes[1])
    ) {
        // Two note descending by >=2
        drawNote(notes[0], long);
        drawNote(notes[1], long);
    } else if (
        (notes.length === 3 &&
            getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
            getNoteValue(notes[1]) - 1 === getNoteValue(notes[2])) ||
        (notes.length === 4 &&
            getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
            getNoteValue(notes[1]) - 1 === getNoteValue(notes[2]) &&
            getNoteValue(notes[2]) - 1 === getNoteValue(notes[3]))
    ) {
        // 3 or 4 note descending
        // Two note consecutive descending
        drawNote(notes[0], false);

        notes.slice(1).forEach((note, noteIndex) => {
            drawDiamond(note, noteIndex === notes.length - 2);
        });
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[1]) === getNoteValue(notes[2])
    ) {
        // 3 note descender as in the compline resonsory
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], long);
    } else if (
        notes.length === 4 &&
        getNoteValue(notes[0]) + 4 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 4 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) + 5 === getNoteValue(notes[3])
    ) {
        // 1556 as in Saints day hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
        drawNote(notes[3], false);
    } else if (
        notes.length === 4 &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[3])
    ) {
        // 5765 as in Saints day hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawDiamond(notes[2], false);
        drawDiamond(notes[3], false);
    } else if (
        notes.length === 4 &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) - 2 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[3])
    ) {
        // 4323 as in annunciation hymn

        drawNote(notes[0], false);
        drawDiamond(notes[1], false);
        drawDiamond(notes[2], false);
        drawNote(notes[3], false);
    } else if (
        notes.length === 4 &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[3])
    ) {
        // 4543 as in annunciation hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawDiamond(notes[2], false);
        drawDiamond(notes[3], false);
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[2])
    ) {
        // 132 as in Sunday evening OT hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[2])
    ) {
        // 345 as in passiontide evening hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
    } else if (
        notes.length === 6 &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) - 3 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) - 2 === getNoteValue(notes[3]) &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[4]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[5])
    ) {
        // 431234 as in passiontide evening hymn
        drawNote(notes[0], false);
        drawDiamond(notes[1], false);
        drawDiamond(notes[2], false);
        drawNote(notes[3], false);
        drawNote(notes[4], false);
        drawNote(notes[5], false);
    } else if (
        notes.length === 5 &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 3 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[3]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[4])
    ) {
        // 35654 as in passiontide evening hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
        drawDiamond(notes[3], false);
        drawDiamond(notes[4], false);
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[2])
    ) {
        // 121 as in Office Hymn "Amen"
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
    } else if (
        notes.length === 5 &&
        getNoteValue(notes[0]) + 3 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[3]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[4])
    ) {
        // 25432 as in Let us bless the Lord Alleluia Alleluia
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawDiamond(notes[2], false);
        drawDiamond(notes[3], false);
        drawDiamond(notes[4], false);
    } else if (
        notes.length === 5 &&
        getNoteValue(notes[0]) + +1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) + 3 === getNoteValue(notes[3]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[4])
    ) {
        // 45675 as in Ascension hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
        drawNote(notes[3], false);
        drawNote(notes[4], false);
    } else if (
        notes.length === 6 &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[2]) &&
        getNoteValue(notes[0]) + 1 === getNoteValue(notes[3]) &&
        getNoteValue(notes[0]) === getNoteValue(notes[4]) &&
        getNoteValue(notes[0]) - 1 === getNoteValue(notes[5])
    ) {
        // 567654 as in Ascension hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
        drawDiamond(notes[3], false);
        drawDiamond(notes[4], false);
        drawDiamond(notes[5], false);
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) + 3 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 2 === getNoteValue(notes[2])
    ) {
        // 476 as in Ascension hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
    } else if (
        notes.length === 3 &&
        getNoteValue(notes[0]) + 4 === getNoteValue(notes[1]) &&
        getNoteValue(notes[0]) + 5 === getNoteValue(notes[2])
    ) {
        // 378 as in Whitsun PM hymn
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawNote(notes[2], false);
    } else if (notes.length === 9) {
        // HACKY SPECIAL CASE FOR "...APPLE OF THINE EYE" - 9 NOTES LONG
        drawNote(notes[0], false);
        drawNote(notes[1], false);
        drawDiamond(notes[2], false);
        drawDiamond(notes[3], false);
        drawDiamond(notes[4], false);
        drawNote(notes[5], false);
        drawNote(notes[6], false);
        drawDiamond(notes[7], false);
        drawDiamond(notes[8], true);
    } else {
        // Any other note(s)
        notes.forEach((note, noteIndex) => {
            drawNote(note, long && noteIndex === notes.length - 1);
        });
    }

    if (result.includes("undefined")) {
        console.log(result, notes);
    }

    return result;
};

export const getGabcFromPointedText = (
    pointedText: PointedText,
    boldMode: BoldMode
) => {
    let gabc = "";

    if (pointedText.clef === "c5") {
        gabc += "(c3)";
    }
    if (pointedText.clef === "c7") {
        gabc += "(c4)";
    }
    if (pointedText.clef === "f5") {
        gabc += "(f3)";
    }

    let verseIsBold = boldMode === "all";

    pointedText.verses.forEach((verse) => {
        verse.firstHalf?.forEach((word) => {
            gabc += " ";

            word.syllables.forEach((syllable) => {
                const notes = getGabcNotesForSyllable(
                    syllable.notes,
                    syllable.long
                );

                if (syllable.dotBefore) {
                    gabc += "£";
                }

                if (syllable.pauseBefore) {
                    gabc += "†(,) ";
                }

                const text = verseIsBold ? `*${syllable.text}*` : syllable.text;

                gabc += text + "(" + notes + ")";
            });
        });

        if (verse.firstHalf) {
            gabc += " $(:)";
        }

        verse.secondHalf?.forEach((word) => {
            gabc += " ";

            word.syllables.forEach((syllable) => {
                const notes = getGabcNotesForSyllable(
                    syllable.notes,
                    syllable.long
                );

                if (syllable.dotBefore) {
                    gabc += "£";
                }

                const text = verseIsBold ? `*${syllable.text}*` : syllable.text;

                gabc += text + "(" + notes + ")";
            });
        });

        gabc += " (::)";

        if (boldMode === "alternate") {
            verseIsBold = !verseIsBold;
        }
    });

    return gabc;
};
